import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Drone,
  DroneOwnership,
  DroneStatus,
  CreateOrganizationDroneDto,
  UpdateOrganizationDroneDto,
  MaintenanceRecord,
  CreateMaintenanceRecordDto,
  UpdateMaintenanceRecordDto,
  DroneManufacturerLookup,
  DroneModelLookup
} from '../models/drone.model';
import { SprayRecordService } from './spray-record.service';
import { OrganizationContextService } from './organization-context.service';
import { OrganizationService } from './organization.service';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DroneService {
  private apiUrl = environment.apiUrl;
  private http = inject(HttpClient);
  private sprayRecordService = inject(SprayRecordService);
  private organizationContext = inject(OrganizationContextService);
  private organizationService = inject(OrganizationService);

  // Signal-based state management
  private dronesSignal = signal<Drone[]>([]);
  private manufacturersSignal = signal<DroneManufacturerLookup[]>([]);
  private modelsSignal = signal<DroneModelLookup[]>([]);
  private maintenanceRecordsSignal = signal<MaintenanceRecord[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  readonly drones = computed(() => this.dronesSignal());
  readonly manufacturers = computed(() => this.manufacturersSignal());
  readonly models = computed(() => this.modelsSignal());
  readonly maintenanceRecords = computed(() => this.maintenanceRecordsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  // Filtered drones by ownership type
  readonly adnDrones = computed(() => this.dronesSignal().filter(d => d.ownership === 'adn'));
  readonly orgDrones = computed(() => this.dronesSignal().filter(d => d.ownership === 'organization'));

  // Stats for dashboard - the API returns per-drone totals on each drone
  readonly totalDrones = computed(() => this.dronesSignal().length);
  readonly activeDrones = computed(() => this.dronesSignal().filter(d => d.status === 'active').length);

  readonly totalFlightHours = computed(() =>
    this.dronesSignal().reduce((sum, d) => sum + (d.totalFlightHours ?? 0), 0)
  );

  readonly totalAcresSprayed = computed(() =>
    this.dronesSignal().reduce((sum, d) => sum + (d.totalAcresSprayed ?? 0), 0)
  );

  constructor() {
    this.loadMockManufacturers();
    this.loadMockModels();
    this.loadMockDrones();
    this.loadMockMaintenanceRecords();
  }

  private loadMockManufacturers(): void {
    const mockManufacturers: DroneManufacturerLookup[] = [
      {
        id: 'mfr-1',
        name: 'DJI',
        logoUrl: 'https://www.dji.com/favicon.ico'
      },
      {
        id: 'mfr-2',
        name: 'Hylio',
        logoUrl: undefined
      },
      {
        id: 'mfr-3',
        name: 'XAG',
        logoUrl: undefined
      },
      {
        id: 'mfr-4',
        name: 'Agras',
        logoUrl: undefined
      }
    ];

    this.manufacturersSignal.set(mockManufacturers);
  }

  private loadMockModels(): void {
    const mockModels: DroneModelLookup[] = [
      // DJI models
      {
        id: 'model-1',
        manufacturerId: 'mfr-1',
        modelName: 'Agras T40',
        maxTakeoffWeightLbs: 101,
        imageUrl: undefined
      },
      {
        id: 'model-2',
        manufacturerId: 'mfr-1',
        modelName: 'Agras T30',
        maxTakeoffWeightLbs: 76,
        imageUrl: undefined
      },
      {
        id: 'model-3',
        manufacturerId: 'mfr-1',
        modelName: 'Agras T20P',
        maxTakeoffWeightLbs: 55,
        imageUrl: undefined
      },
      // Hylio models
      {
        id: 'model-4',
        manufacturerId: 'mfr-2',
        modelName: 'AG-272',
        maxTakeoffWeightLbs: 72,
        imageUrl: undefined
      },
      {
        id: 'model-5',
        manufacturerId: 'mfr-2',
        modelName: 'AG-122',
        maxTakeoffWeightLbs: 45,
        imageUrl: undefined
      },
      // XAG models
      {
        id: 'model-6',
        manufacturerId: 'mfr-3',
        modelName: 'P100 Pro',
        maxTakeoffWeightLbs: 110,
        imageUrl: undefined
      },
      {
        id: 'model-7',
        manufacturerId: 'mfr-3',
        modelName: 'V40',
        maxTakeoffWeightLbs: 65,
        imageUrl: undefined
      }
    ];

    this.modelsSignal.set(mockModels);
  }

  private loadMockDrones(): void {
    // Note: droneId values are used in spray records for flight history linking
    // DJI-AGRAS-001, DJI-AGRAS-002, DJI-AGRAS-003 match spray record mock data
    const mockDrones: Drone[] = [
      // ADN Leased Drones
      {
        id: 'DJI-AGRAS-001',  // Matches spray record droneId
        name: 'ADN Agras T40 #001',
        manufacturerId: 'mfr-1',
        modelId: 'model-1',
        manufacturerName: 'DJI',
        modelName: 'Agras T40',
        serialNumber: 'DJI-AT40-2024-001',
        registrationNumber: 'N12345',
        remoteId: 'RID-ADN-001',
        firmwareVersion: 'v4.2.1',
        ownership: 'adn',
        status: 'active',
        assignedAt: '2024-06-15T00:00:00Z',
        assignmentId: 'assign-1',
        totalFlights: 0,  // Will be computed from spray records
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-01-10T00:00:00Z',
        updatedAt: '2024-12-15T00:00:00Z'
      },
      {
        id: 'DJI-AGRAS-002',  // Matches spray record droneId
        name: 'ADN Agras T30 #002',
        manufacturerId: 'mfr-1',
        modelId: 'model-2',
        manufacturerName: 'DJI',
        modelName: 'Agras T30',
        serialNumber: 'DJI-AT30-2024-002',
        registrationNumber: 'N12346',
        remoteId: 'RID-ADN-002',
        firmwareVersion: 'v3.8.0',
        ownership: 'adn',
        status: 'active',
        assignedAt: '2024-08-01T00:00:00Z',
        assignmentId: 'assign-2',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-02-15T00:00:00Z',
        updatedAt: '2024-12-12T00:00:00Z'
      },
      {
        id: 'DJI-AGRAS-003',  // Matches spray record droneId
        name: 'ADN Agras T40 #003',
        manufacturerId: 'mfr-1',
        modelId: 'model-1',
        manufacturerName: 'DJI',
        modelName: 'Agras T40',
        serialNumber: 'DJI-AT40-2024-003',
        registrationNumber: 'N12347',
        remoteId: 'RID-ADN-003',
        firmwareVersion: 'v4.2.1',
        ownership: 'adn',
        status: 'active',
        assignedAt: '2024-09-10T00:00:00Z',
        assignmentId: 'assign-3',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-03-20T00:00:00Z',
        updatedAt: '2024-12-01T00:00:00Z'
      },
      {
        id: 'adn-4',
        name: 'ADN Hylio AG-272 #004',
        manufacturerId: 'mfr-2',
        modelId: 'model-4',
        manufacturerName: 'Hylio',
        modelName: 'AG-272',
        serialNumber: 'HYL-AG272-2024-001',
        registrationNumber: 'N12348',
        remoteId: 'RID-ADN-004',
        firmwareVersion: 'v2.5.0',
        ownership: 'adn',
        status: 'maintenance',
        assignedAt: '2024-10-01T00:00:00Z',
        assignmentId: 'assign-4',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-04-15T00:00:00Z',
        updatedAt: '2024-12-01T00:00:00Z'
      },
      // Organization-Owned Drones
      {
        id: 'org-1',
        name: 'Farm Sprayer #1',
        manufacturerId: 'mfr-1',
        modelId: 'model-2',
        manufacturerName: 'DJI',
        modelName: 'Agras T30',
        serialNumber: 'CUSTOM-T30-001',
        registrationNumber: 'N54321',
        remoteId: 'RID-ORG-001',
        firmwareVersion: 'v3.8.0',
        ownership: 'organization',
        status: 'active',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-05-01T00:00:00Z',
        updatedAt: '2024-12-10T00:00:00Z'
      },
      {
        id: 'org-2',
        name: 'Orchard Drone',
        manufacturerId: 'mfr-3',
        modelId: 'model-7',
        manufacturerName: 'XAG',
        modelName: 'V40',
        serialNumber: 'XAG-V40-ORG-001',
        firmwareVersion: 'v1.9.2',
        ownership: 'organization',
        status: 'active',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-07-15T00:00:00Z',
        updatedAt: '2024-12-08T00:00:00Z'
      },
      {
        id: 'org-3',
        name: 'Backup Unit',
        manufacturerId: 'mfr-2',
        modelId: 'model-5',
        manufacturerName: 'Hylio',
        modelName: 'AG-122',
        serialNumber: 'HYL-AG122-ORG-001',
        ownership: 'organization',
        status: 'inactive',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-06-01T00:00:00Z',
        updatedAt: '2024-09-15T00:00:00Z'
      }
    ];

    this.dronesSignal.set(mockDrones);
  }

  private loadMockMaintenanceRecords(): void {
    const mockRecords: MaintenanceRecord[] = [
      {
        id: 'maint-1',
        droneId: 'DJI-AGRAS-001',
        type: 'scheduled',
        title: 'Annual Inspection',
        description: 'Comprehensive annual inspection and calibration',
        scheduledDate: '2024-12-20',
        status: 'scheduled',
        performedBy: 'ADN Service Center',
        createdAt: '2024-11-15T00:00:00Z',
        updatedAt: '2024-11-15T00:00:00Z'
      },
      {
        id: 'maint-2',
        droneId: 'adn-4',
        type: 'repair',
        title: 'Motor Replacement',
        description: 'Replace front-left motor due to bearing wear',
        scheduledDate: '2024-12-01',
        completedDate: '2024-12-05',
        status: 'in_progress',
        performedBy: 'Hylio Authorized Service',
        cost: 450,
        createdAt: '2024-11-28T00:00:00Z',
        updatedAt: '2024-12-05T00:00:00Z'
      },
      {
        id: 'maint-3',
        droneId: 'org-1',
        type: 'inspection',
        title: '100-Hour Inspection',
        description: 'Regular 100 flight hour inspection',
        scheduledDate: '2024-11-15',
        completedDate: '2024-11-15',
        status: 'completed',
        performedBy: 'In-house',
        cost: 0,
        notes: 'All systems nominal. Props replaced preventively.',
        createdAt: '2024-11-10T00:00:00Z',
        updatedAt: '2024-11-15T00:00:00Z'
      }
    ];

    this.maintenanceRecordsSignal.set(mockRecords);
  }

  // ==================== API Methods ====================

  /**
   * Load manufacturers and models from the API
   */
  async loadLookups(): Promise<void> {
    try {
      // Load manufacturers
      const mfrResponse = this.http.get<ApiResponse<DroneManufacturerLookup[]>>(
        `${this.apiUrl}/lookups/drone-manufacturers`
      );
      const mfrResult = await firstValueFrom(mfrResponse);
      if (mfrResult.success && mfrResult.data) {
        this.manufacturersSignal.set(mfrResult.data);
      }

      // Load models
      const modelResponse = this.http.get<ApiResponse<DroneModelLookup[]>>(
        `${this.apiUrl}/lookups/drone-models`
      );
      const modelResult = await firstValueFrom(modelResponse);
      if (modelResult.success && modelResult.data) {
        this.modelsSignal.set(modelResult.data);
      }
    } catch (error: any) {
      console.error('[DroneService] Error loading lookups:', error);
    }
  }

  /**
   * Load drones assigned to the current organization from the API
   * Includes both ADN-assigned drones and organization-owned drones
   */
  async loadOrganizationDrones(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('[DroneService] No organization selected');
      return;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = this.http.get<ApiResponse<any[]>>(
        `${this.apiUrl}/organizations/${orgId}/drones`
      );

      const result = await firstValueFrom(response);

      if (!result.success) {
        throw new Error(result.error || 'Failed to load drones');
      }

      // Map API response to Drone model
      // API returns both ADN drones and organization-owned drones with 'ownership' field
      const drones: Drone[] = (result.data || []).map(apiDrone => ({
        id: apiDrone.id,
        name: apiDrone.name,
        manufacturerId: apiDrone.manufacturerId,
        modelId: apiDrone.modelId,
        manufacturerName: apiDrone.manufacturerName,
        modelName: apiDrone.modelName,
        serialNumber: apiDrone.serialNumber,
        registrationNumber: apiDrone.nNumber,
        remoteId: apiDrone.remoteId,
        firmwareVersion: apiDrone.firmwareVersion,
        ownership: apiDrone.ownership as DroneOwnership, // 'adn' or 'organization'
        status: apiDrone.status as DroneStatus,
        assignedAt: apiDrone.assignedAt,
        assignmentId: apiDrone.assignmentId,
        totalFlights: apiDrone.totalFlights ?? 0,
        totalAcresSprayed: apiDrone.totalAcresSprayed ?? 0,
        totalFlightHours: apiDrone.totalFlightHours ?? 0,
        lastFlightDate: apiDrone.lastFlightDate ?? undefined,
        createdAt: apiDrone.createdAt,
        updatedAt: apiDrone.updatedAt
      }));

      this.dronesSignal.set(drones);
    } catch (error: any) {
      console.error('[DroneService] Error loading organization drones:', error);
      this.errorSignal.set(error.message || 'Failed to load drones');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  // ==================== Drone CRUD Operations ====================

  getDrones(): Drone[] {
    return this.drones();
  }

  getDroneById(id: string): Drone | undefined {
    return this.dronesSignal().find(d => d.id === id);
  }

  /**
   * Fetch a single drone from the API by ID.
   * Used when drone may not be in local cache (e.g., navigating from activity link).
   */
  async fetchDroneById(id: string): Promise<Drone | null> {
    try {
      const response = this.http.get<ApiResponse<any>>(
        `${this.apiUrl}/drones/${id}`
      );

      const result = await firstValueFrom(response);

      if (!result.success || !result.data) {
        return null;
      }

      const apiDrone = result.data;
      const drone: Drone = {
        id: apiDrone.id,
        name: apiDrone.name,
        manufacturerId: apiDrone.manufacturerId,
        modelId: apiDrone.modelId,
        manufacturerName: apiDrone.manufacturerName,
        modelName: apiDrone.modelName,
        serialNumber: apiDrone.serialNumber,
        registrationNumber: apiDrone.nNumber,
        remoteId: apiDrone.remoteId,
        firmwareVersion: apiDrone.firmwareVersion,
        ownership: apiDrone.ownership as DroneOwnership,
        status: apiDrone.status as DroneStatus,
        assignedAt: apiDrone.assignedAt,
        assignmentId: apiDrone.assignmentId,
        totalFlights: apiDrone.totalFlights ?? 0,
        totalAcresSprayed: apiDrone.totalAcresSprayed ?? 0,
        totalFlightHours: apiDrone.totalFlightHours ?? 0,
        lastFlightDate: apiDrone.lastFlightDate ?? undefined,
        createdAt: apiDrone.createdAt,
        updatedAt: apiDrone.updatedAt
      };

      // Add to local cache if not already present
      if (!this.dronesSignal().find(d => d.id === id)) {
        this.dronesSignal.update(drones => [...drones, drone]);
      }

      return drone;
    } catch (error: any) {
      console.error('[DroneService] Error fetching drone by ID:', error);
      return null;
    }
  }

  /**
   * Get models filtered by manufacturer ID (for cascading dropdown)
   */
  getModelsByManufacturer(manufacturerId: string): DroneModelLookup[] {
    return this.modelsSignal().filter(m => m.manufacturerId === manufacturerId);
  }

  /**
   * Get manufacturer name by ID
   */
  getManufacturerName(manufacturerId: string): string {
    const manufacturer = this.manufacturersSignal().find(m => m.id === manufacturerId);
    return manufacturer?.name || 'Unknown';
  }

  /**
   * Get model name by ID
   */
  getModelName(modelId: string): string {
    const model = this.modelsSignal().find(m => m.id === modelId);
    return model?.modelName || 'Unknown';
  }

  /**
   * Create a new organization-owned drone.
   * ADN drones cannot be created through the web portal.
   */
  async createDrone(dto: CreateOrganizationDroneDto): Promise<Drone> {
    try {
      // Map frontend DTO to backend DTO format
      const backendDto = {
        name: dto.name,
        manufacturerId: dto.manufacturerId,
        modelId: dto.modelId,
        serialNumber: dto.serialNumber,
        nNumber: dto.registrationNumber,
        remoteId: dto.remoteId,
        firmwareVersion: dto.firmwareVersion,
        status: dto.status || 'active'
      };

      // Call API to create drone
      const apiDrone = await this.organizationService.createMyOrganizationDrone(backendDto);

      // Convert to frontend Drone model
      const newDrone: Drone = {
        id: apiDrone.id,
        name: apiDrone.name,
        manufacturerId: apiDrone.manufacturerId!,
        modelId: apiDrone.modelId!,
        manufacturerName: apiDrone.manufacturerName,
        modelName: apiDrone.modelName,
        serialNumber: apiDrone.serialNumber,
        registrationNumber: apiDrone.nNumber,
        remoteId: apiDrone.remoteId,
        firmwareVersion: apiDrone.firmwareVersion,
        ownership: 'organization',
        status: apiDrone.status as DroneStatus,
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Add to local state
      this.dronesSignal.update(drones => [...drones, newDrone]);
      return newDrone;
    } catch (error: any) {
      console.error('[DroneService] Error creating drone:', error);
      throw error;
    }
  }

  /**
   * Update an organization-owned drone.
   * ADN drones cannot be edited through the web portal.
   */
  async updateDrone(id: string, dto: UpdateOrganizationDroneDto): Promise<Drone | null> {
    const drone = this.getDroneById(id);

    // Cannot edit ADN drones
    if (!drone || drone.ownership === 'adn') {
      return null;
    }

    try {
      // Map frontend DTO to backend DTO format
      const backendDto: any = {};
      if (dto.name !== undefined) backendDto.name = dto.name;
      if (dto.manufacturerId !== undefined) backendDto.manufacturerId = dto.manufacturerId;
      if (dto.modelId !== undefined) backendDto.modelId = dto.modelId;
      if (dto.serialNumber !== undefined) backendDto.serialNumber = dto.serialNumber;
      if (dto.registrationNumber !== undefined) backendDto.nNumber = dto.registrationNumber;
      if (dto.remoteId !== undefined) backendDto.remoteId = dto.remoteId;
      if (dto.firmwareVersion !== undefined) backendDto.firmwareVersion = dto.firmwareVersion;
      if (dto.status !== undefined) backendDto.status = dto.status;

      // Call API to update drone
      const apiDrone = await this.organizationService.updateMyOrganizationDrone(id, backendDto);

      // Convert to frontend Drone model
      const updatedDrone: Drone = {
        ...drone,
        name: apiDrone.name,
        manufacturerId: apiDrone.manufacturerId!,
        modelId: apiDrone.modelId!,
        manufacturerName: apiDrone.manufacturerName,
        modelName: apiDrone.modelName,
        serialNumber: apiDrone.serialNumber,
        registrationNumber: apiDrone.nNumber,
        remoteId: apiDrone.remoteId,
        firmwareVersion: apiDrone.firmwareVersion,
        status: apiDrone.status as DroneStatus,
        updatedAt: new Date().toISOString()
      };

      // Update local state
      const index = this.dronesSignal().findIndex(d => d.id === id);
      if (index !== -1) {
        this.dronesSignal.update(drones => {
          const newDrones = [...drones];
          newDrones[index] = updatedDrone;
          return newDrones;
        });
      }

      return updatedDrone;
    } catch (error: any) {
      console.error('[DroneService] Error updating drone:', error);
      throw error;
    }
  }

  /**
   * Delete an organization-owned drone.
   * ADN drones cannot be deleted through the web portal.
   */
  async deleteDrone(id: string): Promise<boolean> {
    const drone = this.getDroneById(id);

    // Cannot delete ADN drones
    if (!drone || drone.ownership === 'adn') {
      return false;
    }

    try {
      // Call API to delete drone
      await this.organizationService.deleteMyOrganizationDrone(id);

      // Update local state
      this.dronesSignal.update(drones => drones.filter(d => d.id !== id));

      // Also delete associated maintenance records
      this.maintenanceRecordsSignal.update(records =>
        records.filter(r => r.droneId !== id)
      );

      return true;
    } catch (error: any) {
      console.error('[DroneService] Error deleting drone:', error);
      throw error;
    }
  }

  /**
   * Check if a drone can be edited (organization-owned only)
   */
  canEdit(drone: Drone): boolean {
    return drone.ownership === 'organization';
  }

  /**
   * Check if a drone can be deleted (organization-owned only)
   */
  canDelete(drone: Drone): boolean {
    return drone.ownership === 'organization';
  }

  // ==================== Maintenance Record Operations ====================

  getMaintenanceByDroneId(droneId: string): MaintenanceRecord[] {
    return this.maintenanceRecordsSignal().filter(r => r.droneId === droneId);
  }

  getUpcomingMaintenance(droneId: string): MaintenanceRecord | undefined {
    const records = this.getMaintenanceByDroneId(droneId);
    const upcoming = records
      .filter(r => r.status === 'scheduled' || r.status === 'in_progress')
      .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime());
    return upcoming[0];
  }

  createMaintenanceRecord(dto: CreateMaintenanceRecordDto): MaintenanceRecord {
    const newRecord: MaintenanceRecord = {
      id: 'maint-' + Date.now(),
      droneId: dto.droneId,
      type: dto.type,
      title: dto.title,
      description: dto.description,
      scheduledDate: dto.scheduledDate,
      status: 'scheduled',
      performedBy: dto.performedBy,
      cost: dto.cost,
      notes: dto.notes,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.maintenanceRecordsSignal.update(records => [...records, newRecord]);
    return newRecord;
  }

  updateMaintenanceRecord(id: string, dto: UpdateMaintenanceRecordDto): MaintenanceRecord | null {
    const index = this.maintenanceRecordsSignal().findIndex(r => r.id === id);
    if (index === -1) {
      return null;
    }

    const updatedRecord: MaintenanceRecord = {
      ...this.maintenanceRecordsSignal()[index],
      ...dto,
      updatedAt: new Date().toISOString()
    };

    this.maintenanceRecordsSignal.update(records => {
      const newRecords = [...records];
      newRecords[index] = updatedRecord;
      return newRecords;
    });

    return updatedRecord;
  }

  deleteMaintenanceRecord(id: string): boolean {
    const index = this.maintenanceRecordsSignal().findIndex(r => r.id === id);
    if (index === -1) {
      return false;
    }

    this.maintenanceRecordsSignal.update(records => records.filter(r => r.id !== id));
    return true;
  }

  // ==================== Stats for Dashboard ====================

  getDroneStats(): { total: number; active: number; flightHours: number; acresSprayed: number } {
    return {
      total: this.totalDrones(),
      active: this.activeDrones(),
      flightHours: this.totalFlightHours(),
      acresSprayed: this.totalAcresSprayed()
    };
  }

  // ==================== Drone Stats ====================

  /**
   * Get flight stats for a drone.
   * The API aggregates these per drone, so nothing is computed here.
   */
  getDroneFlightStats(droneId: string): {
    totalFlights: number;
    totalAcresSprayed: number;
    totalFlightHours: number;
    lastFlightDate: string | undefined;
  } {
    const drone = this.getDroneById(droneId);

    return {
      totalFlights: drone?.totalFlights ?? 0,
      totalAcresSprayed: drone?.totalAcresSprayed ?? 0,
      totalFlightHours: drone?.totalFlightHours ?? 0,
      lastFlightDate: drone?.lastFlightDate
    };
  }

  /**
   * Get spray records filtered by drone ID (for flight history view)
   */
  getSprayRecordsByDroneId(droneId: string): string[] {
    const records = this.sprayRecordService.records();
    const matchingRecordIds: string[] = [];

    for (const record of records) {
      const flights = record.droneFlights || [];
      const hasDrone = flights.some((flight: any) => flight.droneId === droneId);
      if (hasDrone) {
        matchingRecordIds.push(record.id);
      }
    }

    return matchingRecordIds;
  }

  /**
   * Get count of flights for a specific drone
   */
  getFlightCountForDrone(droneId: string): number {
    return this.getDroneFlightStats(droneId).totalFlights;
  }
}
